import {
  AgedTrialBalances,
  PaginatedCurrentPricesReports,
  PaginatedCustOrderHistoryReports,
  PaginatedDebtorBalanceReports,
  PaginatedGrossProfitMarginReports,
  PaginatedProductBalanceReports,
  PaginatedRecentOrderHistoryReports,
  PaginatedSalesAnlysisByMonthReports,
  PaginatedSalesAnlysisByQtrReports,
  PaginatedSalesPersonByMonthReports,
  PaginatedSalesPersonReports,
  PaginatedSalesProfitMarginsReports,
  PaginatedSoHReports,
} from "types/model.types";
import { authApi } from "./";

const apiReportPrefix = "report";

export const getRptSalesPersonByMonth =
  async (): Promise<void | PaginatedSalesPersonByMonthReports> => {
    const { data } = await authApi.post<PaginatedSalesPersonByMonthReports>(
      `${apiReportPrefix}/salesPersonByMonth`,
      {},
    );
    return data ?? [];
  };

export const getRptSalesPerson = async (
  fromDate: Date | null,
  toDate: Date | null,
): Promise<void | PaginatedSalesPersonReports> => {
  const { data } = await authApi.post<PaginatedSalesPersonReports>(
    `${apiReportPrefix}/salesPerson`,
    { fromDate, toDate },
  );
  return data ?? [];
};
export const getStockOnHand = async (
  pageNumber = 1,
  pageSize = 30,
  fromCode: number | null = null,
  toCode: number | null = null,
  downloadCsv = false,
): Promise<void | PaginatedSoHReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/stockOnHand`,
      {
        pageNumber,
        pageSize,
        downloadCsv,
        fromCode,
        toCode,
      },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `product-stock-level.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  }

  const { data } = await authApi.post<PaginatedSoHReports>(
    `${apiReportPrefix}/stockOnHand`,
    {
      pageNumber,
      pageSize,
      downloadCsv,
      fromCode,
      toCode,
    },
  );
  return data ?? [];
};
export const getCustomerOrderHistory = async (
  debtorId = 1,
  year: number,
): Promise<void | PaginatedCustOrderHistoryReports> => {
  const { data } = await authApi.post<PaginatedCustOrderHistoryReports>(
    `${apiReportPrefix}/customerOrderHistory`,
    {
      debtorId,
      year,
    },
  );
  return data ?? [];
};
export const getRecentOrderDetails = async (
  fromCode: number | null,
  toCode: number | null,
  year: number | null,
  pageSize?: number | null,
  pageNumber?: number | null,
  downloadCsv = false,
): Promise<void | PaginatedRecentOrderHistoryReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/recentOrderDetails`,
      { fromCode, toCode, year, downloadCsv },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `customer_orders_${year}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  } else {
    // JSON branch
    const { data } = await authApi.post<PaginatedRecentOrderHistoryReports>(
      `${apiReportPrefix}/recentOrderDetails`,
      { fromCode, toCode, year, pageNumber, pageSize },
    );
    return data ?? [];
  }
};

export const getCurrentPrices = async (
  fromCode: number | null,
  toCode: number | null,
  pageSize?: number | null,
  pageNumber?: number | null,
  downloadCsv = false,
): Promise<void | PaginatedCurrentPricesReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/currentPriceList`,
      { fromCode, toCode, downloadCsv },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `current_price.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  } else {
    // JSON branch
    const { data } = await authApi.post<PaginatedCurrentPricesReports>(
      `${apiReportPrefix}/currentPriceList`,
      { fromCode, toCode, pageNumber, pageSize },
    );
    return data ?? [];
  }
};

export const getGrossProfitMargins = async (
  fromCode: number | null,
  toCode: number | null,
  pageSize?: number | null,
  pageNumber?: number | null,
  downloadCsv = false,
): Promise<void | PaginatedGrossProfitMarginReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/grossProfitMarginSummary`,
      { fromCode, toCode, downloadCsv },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `gross-profit-margins.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  } else {
    // JSON branch
    const { data } = await authApi.post<PaginatedGrossProfitMarginReports>(
      `${apiReportPrefix}/grossProfitMarginSummary`,
      { fromCode, toCode, pageNumber, pageSize },
    );
    return data ?? [];
  }
};
export const getDebtorBalanceSummary =
  async (): Promise<void | PaginatedDebtorBalanceReports> => {
    const { data } = await authApi.post<PaginatedDebtorBalanceReports>(
      `${apiReportPrefix}/debtorBalanceSummary`,
      {},
    );
    return data ?? [];
  };
export const getProductBalanceSummary =
  async (): Promise<void | PaginatedProductBalanceReports> => {
    const { data } = await authApi.post<PaginatedProductBalanceReports>(
      `${apiReportPrefix}/productBalanceSummary`,
      {},
    );
    return data ?? [];
  };

export const getSalesProfitMarginByMonth = async (
  year: number,
  fromCode: number | null,
  toCode: number | null,
  pageSize?: number | null,
  pageNumber?: number | null,
  downloadCsv = false,
): Promise<void | PaginatedSalesProfitMarginsReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/salesProfitMarginByMonth`,
      { fromCode, toCode, downloadCsv, year },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-profilt-margin-by-month.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  } else {
    // JSON branch
    const { data } = await authApi.post<PaginatedSalesProfitMarginsReports>(
      `${apiReportPrefix}/salesProfitMarginByMonth`,
      { fromCode, toCode, pageNumber, pageSize, year },
    );
    return data ?? [];
  }
};
export const getSalesProfitMarginYr2Date = async (
  year: number,
  fromCode: number | null,
  toCode: number | null,
  pageSize?: number | null,
  pageNumber?: number | null,
  downloadCsv = false,
): Promise<void | PaginatedSalesProfitMarginsReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/salesProfitMarginYearToDate`,
      { fromCode, toCode, downloadCsv, year },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-profilt-margin-year-to-date.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  } else {
    // JSON branch
    const { data } = await authApi.post<PaginatedSalesProfitMarginsReports>(
      `${apiReportPrefix}/salesProfitMarginYearToDate`,
      { fromCode, toCode, pageNumber, pageSize, year },
    );
    return data ?? [];
  }
};
export const getSalesAnalysisByMonth = async (
  year: number,
  fromCode: number | null,
  toCode: number | null,
  pageSize?: number | null,
  pageNumber?: number | null,
  downloadCsv = false,
): Promise<void | PaginatedSalesAnlysisByMonthReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/salesAnalysisByMonth`,
      { fromCode, toCode, downloadCsv, year },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-analysis-by-month.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  } else {
    // JSON branch
    const { data } = await authApi.post<PaginatedSalesAnlysisByMonthReports>(
      `${apiReportPrefix}/salesAnalysisByMonth`,
      { fromCode, toCode, pageNumber, pageSize },
    );
    return data ?? [];
  }
};

export const getSalesAnalysisByQuarter = async (
  year: number,
  fromCode: number | null,
  toCode: number | null,
  pageSize?: number | null,
  pageNumber?: number | null,
  downloadCsv = false,
): Promise<void | PaginatedSalesAnlysisByQtrReports> => {
  if (downloadCsv) {
    // CSV download branch
    const response = await authApi.post(
      `${apiReportPrefix}/salesAnalysisByQuarter`,
      { fromCode, toCode, downloadCsv, year },
      { responseType: "blob" },
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-analysis-by-quarter.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return; // nothing to return for CSV
  } else {
    // JSON branch
    const { data } = await authApi.post<PaginatedSalesAnlysisByQtrReports>(
      `${apiReportPrefix}/salesAnalysisByQuarter`,
      { fromCode, toCode, pageNumber, pageSize },
    );
    return data ?? [];
  }
};

export const getAgedTrialBalanceReport =
  async (): Promise<void | AgedTrialBalances> => {
    const { data } = await authApi.post<AgedTrialBalances>(
      `${apiReportPrefix}/agedTrialBalanceReport`,
      {},
    );
    return data ?? [];
  };

export const getAgedTrialBalanceReportByDebtor = async (
  debtorId: number,
  includeInvoices = false,
): Promise<void | AgedTrialBalances> => {
  const { data } = await authApi.post<AgedTrialBalances>(
    `${apiReportPrefix}/agedTrialBalanceReportByDebtor`,
    { debtorId, includeInvoices },
  );
  return data ?? [];
};
